/**
 * Categories Page - with Tabs for Products and Services
 */
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import api from '../../lib/api';

import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { DataGrid } from '../../components/ui/DataGrid';
import { PageHeader } from '../../components/ui/PageHeader';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { Plus, FolderTree, Edit, Trash2, Package, Utensils, Wine, IceCream, Sandwich, UtensilsCrossed, Download, FileText, FileSpreadsheet, ChevronDown, CheckSquare, Briefcase, Layers } from 'lucide-react';

import CategoryModal from './CategoryModal';
import './styles.css';

const iconMap = { Package, Utensils, Wine, IceCream, Sandwich, UtensilsCrossed, Briefcase, Layers };

export default function Categories() {
  const { t } = useTranslation();
  const gridRef = useRef(null);

  // Tab state
  const [activeTab, setActiveTab] = useState('product');

  // Data state
  const [categories, setCategories] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  // Server-side pagination state
  const [totalItems, setTotalItems] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [sortBy, setSortBy] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [filters, setFilters] = useState({});

  // Selection state
  const [selectedCount, setSelectedCount] = useState(0);

  // Load categories with server-side params
  const loadCategories = useCallback(async () => {
    try {
      setLoading(true);
      
      // Build filters object - always filter by type
      const apiFilters = { type: activeTab };
      if (filters.name) apiFilters.name = filters.name;
      if (filters.status && filters.status !== 'all') apiFilters.is_active = filters.status === 'active';

      const response = await api.post('/categories/list', {
        pageNumber,
        pageSize,
        sortBy,
        sortDirection,
        filters: apiFilters,
      });

      if (response.data?.success) {
        setCategories(response.data.data?.items || []);
        setTotalItems(response.data.data?.totalItems || 0);
      } else {
        setCategories(response.data?.data?.items || response.data || []);
        setTotalItems(response.data?.data?.totalItems || response.data?.length || 0);
      }
    } catch (err) {
      console.error('Error loading categories:', err);
      toast.error(t('errors.generic'));
    } finally {
      setLoading(false);
    }
  }, [pageNumber, pageSize, sortBy, sortDirection, filters, activeTab, t]);

  // Load all categories for parent selector
  const loadAllCategories = useCallback(async () => {
    try {
      const res = await api.get(`/categories?type=${activeTab}`);
      setAllCategories(res.data || []);
    } catch (err) {
      console.error('Error loading all categories:', err);
    }
  }, [activeTab]);

  useEffect(() => {
    loadAllCategories();
  }, [loadAllCategories]);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  // Reset page when tab changes
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setPageNumber(1);
    setFilters({});
  };

  // Handle page change from grid
  const handlePageChange = useCallback((newPage, newPageSize) => {
    setPageNumber(newPage);
    setPageSize(newPageSize);
  }, []);

  // Handle sort change from grid
  const handleSortChange = useCallback((newSortBy, newSortDirection) => {
    if (newSortBy && newSortDirection) {
      setSortBy(newSortBy);
      setSortDirection(newSortDirection);
    }
  }, []);

  // Handle filter search
  const handleSearch = useCallback(() => {
    setPageNumber(1);
    loadCategories();
  }, [loadCategories]);

  const handleClearFilters = useCallback(() => {
    setFilters({});
    setPageNumber(1);
  }, []);

  const handleSave = async (data) => {
    try {
      setSaving(true);
      // Force type based on active tab
      const categoryData = { ...data, type: activeTab };
      
      if (editingCategory?.id) {
        await api.put(`/categories/${editingCategory.id}`, categoryData);
      } else {
        await api.post('/categories', categoryData);
      }
      toast.success(t('categories.saveSuccess'));
      setModalOpen(false);
      setEditingCategory(null);
      await loadCategories();
      await loadAllCategories();
    } catch (err) {
      console.error('Error saving category:', err);
      toast.error(err.response?.data?.detail || t('errors.generic'));
    } finally {
      setSaving(false);
    }
  };

  // Handle modal close - reset editingCategory to null
  const handleModalOpenChange = (open) => {
    setModalOpen(open);
    if (!open) {
      setTimeout(() => setEditingCategory(null), 100);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await api.delete(`/categories/${deleteId}`);
      toast.success(t('categories.deleteSuccess'));
      setDeleteId(null);
      await loadCategories();
      await loadAllCategories();
    } catch (err) {
      toast.error(t('errors.generic'));
    }
  };

  // Export handlers
  const handleExportCSV = useCallback((onlySelected = false) => {
    if (onlySelected && selectedCount === 0) {
      toast.error('Selecione pelo menos um item para exportar');
      return;
    }
    gridRef.current?.exportCSV(onlySelected);
  }, [selectedCount]);

  const handleExportJSON = useCallback((onlySelected = false) => {
    if (onlySelected && selectedCount === 0) {
      toast.error('Selecione pelo menos um item para exportar');
      return;
    }
    const data = gridRef.current?.exportJSON(onlySelected);
    if (!data?.length) {
      toast.error('Nenhum dado para exportar');
      return;
    }
    const jsonStr = JSON.stringify(data, null, 2);
    const blob = new Blob([jsonStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `categorias_${activeTab}_${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, [selectedCount, activeTab]);

  // Track selection changes
  const handleSelectionChanged = useCallback(() => {
    const count = gridRef.current?.getSelectedCount() || 0;
    setSelectedCount(count);
  }, []);

  const parentCategories = useMemo(() => 
    allCategories.filter(c => !c.parent_id), 
    [allCategories]
  );

  const filterDefinitions = useMemo(() => [
    {
      key: 'name',
      label: t('categories.name'),
      type: 'text',
      placeholder: 'Buscar categoria...',
    },
    {
      key: 'status',
      label: t('common.status'),
      type: 'select',
      placeholder: 'Todos',
      options: [
        { value: 'all', label: 'Todos' },
        { value: 'active', label: 'Ativo' },
        { value: 'inactive', label: 'Inativo' },
      ],
    },
  ], [t]);

  const columnDefs = useMemo(() => [
    {
      field: 'name',
      headerName: t('categories.name'),
      flex: 2,
      sortable: true,
      cellRenderer: (params) => {
        const Icon = iconMap[params.data.icon] || Package;
        const parentName = params.data.parent_id 
          ? allCategories.find(c => c.id === params.data.parent_id)?.name 
          : null;
        return (
          <div className="flex items-center gap-3">
            <div 
              className="w-8 h-8 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: params.data.color + '20', color: params.data.color }}
            >
              <Icon className="h-4 w-4" />
            </div>
            <div>
              <span className="font-medium">{params.value}</span>
              {parentName && (
                <span className="text-xs text-muted-foreground ml-2">
                  (sub de {parentName})
                </span>
              )}
            </div>
          </div>
        );
      },
    },
    {
      field: 'description',
      headerName: t('categories.description'),
      flex: 2,
      sortable: true,
      valueFormatter: (params) => params.value || '-',
    },
    {
      field: 'is_active',
      headerName: t('common.status'),
      flex: 0.8,
      sortable: true,
      cellRenderer: (params) => (
        <span className={params.value ? 'datagrid-status-active' : 'datagrid-status-inactive'}>
          {params.value ? t('common.active') : t('common.inactive')}
        </span>
      ),
    },
    {
      field: 'actions',
      headerName: t('common.actions'),
      flex: 0.8,
      sortable: false,
      filter: false,
      cellRenderer: (params) => (
        <div className="datagrid-actions">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => {
              setEditingCategory(params.data);
              setModalOpen(true);
            }}
          >
            <Edit className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setDeleteId(params.data.id)}
          >
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        </div>
      ),
    },
  ], [t, allCategories]);

  // Export actions dropdown
  const ExportActions = () => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm">
          <Download className="h-4 w-4 mr-2" />
          Exportar
          {selectedCount > 0 && (
            <Badge variant="secondary" className="ml-2">{selectedCount}</Badge>
          )}
          <ChevronDown className="h-3 w-3 ml-2" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => handleExportCSV(false)}>
          <FileText className="h-4 w-4 mr-2" />
          Exportar Tudo (CSV)
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleExportJSON(false)}>
          <FileSpreadsheet className="h-4 w-4 mr-2" />
          Exportar Tudo (JSON)
        </DropdownMenuItem>
        {selectedCount > 0 && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => handleExportCSV(true)}>
              <CheckSquare className="h-4 w-4 mr-2" />
              Exportar {selectedCount} Selecionados (CSV)
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleExportJSON(true)}>
              <CheckSquare className="h-4 w-4 mr-2" />
              Exportar {selectedCount} Selecionados (JSON)
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  const tabLabel = activeTab === 'product' ? 'Produtos' : activeTab === 'service' ? 'Serviços' : 'Combos';

  return (
    <div className="categories-page" data-testid="categories-page">
      <PageHeader
        title={t('categories.title')}
        icon={FolderTree}
        subtitle={`${totalItems} categorias de ${tabLabel.toLowerCase()} cadastradas`}
        actions={
          <div className="flex items-center gap-2">
            <ExportActions />
            <Button onClick={() => { setEditingCategory(null); setModalOpen(true); }} data-testid="new-category-btn">
              <Plus className="h-4 w-4 mr-2" />
              Nova Categoria
            </Button>
          </div>
        }
        filters={filterDefinitions}
        filterValues={filters}
        onFilterChange={setFilters}
        onSearch={handleSearch}
        onClearFilters={handleClearFilters}
        showExport={false}
      />

      {/* Tabs for Product, Service and Combo categories */}
      <Tabs value={activeTab} onValueChange={handleTabChange} className="mb-4">
        <TabsList>
          <TabsTrigger value="product" className="flex items-center gap-2" data-testid="tab-products">
            <Package className="h-4 w-4" />
            Produtos
          </TabsTrigger>
          <TabsTrigger value="service" className="flex items-center gap-2" data-testid="tab-services">
            <Briefcase className="h-4 w-4" />
            Serviços
          </TabsTrigger>
          <TabsTrigger value="combo" className="flex items-center gap-2" data-testid="tab-combos">
            <Layers className="h-4 w-4" />
            Combos
          </TabsTrigger>
        </TabsList>
      </Tabs>

      <DataGrid
        ref={gridRef}
        title={`Categorias de ${tabLabel}`}
        columnDefs={columnDefs}
        rowData={categories}
        loading={loading}
        enableSelection={true}
        getRowId={(params) => params.data.id}
        serverSide={true}
        totalItems={totalItems}
        pageNumber={pageNumber}
        onPageChange={handlePageChange}
        onSortChange={handleSortChange}
        sortBy={sortBy}
        sortDirection={sortDirection}
        paginationPageSize={pageSize}
        onSelectionChanged={handleSelectionChanged}
      />

      <CategoryModal
        open={modalOpen}
        onOpenChange={handleModalOpenChange}
        category={editingCategory}
        categories={parentCategories}
        categoryType={activeTab}
        onSave={handleSave}
        loading={saving}
      />

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('common.confirm')}</AlertDialogTitle>
            <AlertDialogDescription>{t('categories.deleteConfirm')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>{t('common.delete')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
